import React from 'react';
import { useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { getCurrentUserFirstListing } from '../../ducks/user.duck';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';
import { LISTING_PAGE_PARAM_TYPE_EDIT } from '../../util/urlHelpers';

import NamedLink from '../NamedLink/NamedLink';
import ExternalLink from '../ExternalLink/ExternalLink';
import Logo from '../Logo/Logo';
import IconSocialMediaFacebook from '../IconSocialMediaFacebook/IconSocialMediaFacebook';
import IconSocialMediaInstagram from '../IconSocialMediaInstagram/IconSocialMediaInstagram';
import IconSocialMediaTwitter from '../IconSocialMediaTwitter/IconSocialMediaTwitter';
import IconSocialMediaTiktok from '../IconSocialMediaTiktok/IconSocialMediaTiktok';

import css from './Footer.module.css';

const EXPLORE_LINKS = [
  { name: 'SearchPage', label: 'Find an owner' },
  { label: 'Airbnb business owners', search: '?pub_categoryLevel1=airbnb' },
  { label: 'Nail Salon business owners', search: '?pub_categoryLevel1=nail-salon' },
  { label: 'Kids Activities business owners', search: '?pub_categoryLevel1=kids-activities' },
  { label: 'Laundromat business owners', search: '?pub_categoryLevel1=laundromats' },
  { label: 'Event Space business owners', search: '?pub_categoryLevel1=event-space' },
  { label: 'Coffee Shop business owners', search: '?pub_categoryLevel1=coffee-shop' },
  { label: 'Fitness Studio business owners', search: '?pub_categoryLevel1=fitness-studio' },
  { name: 'AboutPage', label: 'About us' },
];

// "Profile settings" resolves like the topbar's link: an owner's existing listing editor
// when they have a listing, the new-listing flow otherwise, and the profile settings page
// for customers.
const profileSettingsLink = (config, currentUser, firstListing) => {
  const { provider: isProvider } = getCurrentUserTypeRoles(config, currentUser);
  if (currentUser && !isProvider) {
    return { name: 'ProfileSettingsPage' };
  }
  return isProvider && firstListing?.id && firstListing?.slug
    ? {
        name: 'EditListingPage',
        params: {
          id: firstListing.id,
          slug: firstListing.slug,
          type: LISTING_PAGE_PARAM_TYPE_EDIT,
          tab: 'details',
        },
      }
    : { name: 'NewListingPage' };
};

const LEGAL_LINKS = [
  { name: 'TermsOfServicePage', label: 'Terms of service' },
  { name: 'PrivacyPolicyPage', label: 'Privacy policy' },
];

const Footer = () => {
  const config = useConfiguration();
  const marketplaceName = config?.marketplaceName || 'DoneThat';
  const currentUser = useSelector(state => state.user?.currentUser);
  const firstListing = useSelector(getCurrentUserFirstListing);
  const expertLinks = [
    { name: 'ApplyPage', label: 'Become an expert' },
    { ...profileSettingsLink(config, currentUser, firstListing), label: 'Profile settings' },
    { name: 'DashboardPage', label: 'Dashboard' },
  ];

  const facebookUrl = config?.siteFacebookPage;
  const instagramUrl = config?.siteInstagramPage;
  const tiktokUrl = config?.siteTiktokPage;
  const twitterHandle = config?.siteTwitterHandle;
  const twitterUrl = twitterHandle
    ? `https://twitter.com/${twitterHandle.replace(/^@/, '')}`
    : null;

  const year = new Date().getFullYear();

  return (
    <footer className={css.footer}>
      <div className={css.pattern} aria-hidden />
      <div className={css.orb} aria-hidden />

      <div className={css.inner}>
        {/* ===== Top: Brand ===== */}
        <div className={css.top}>
          <div className={css.brandBlock}>
            <NamedLink name="LandingPage" className={css.brandLink}>
              <Logo layout="desktop" className={css.footerLogo} />
            </NamedLink>
            <p className={css.brandTagline}>
              Book video calls with experienced <em>business owners</em> who&apos;ve actually done
              it
            </p>
          </div>
        </div>

        {/* ===== Middle: Link columns ===== */}
        <nav className={css.linkGrid} aria-label="Footer">
          <div className={css.linkColumn}>
            <h4 className={css.columnTitle}>Explore</h4>
            <ul className={css.linkList}>
              {EXPLORE_LINKS.map((link, i) => (
                <li key={link.name || link.search || i}>
                  {link.search ? (
                    <NamedLink name="SearchPage" to={{ search: link.search }} className={css.link}>
                      {link.label}
                    </NamedLink>
                  ) : (
                    <NamedLink name={link.name} className={css.link}>
                      {link.label}
                    </NamedLink>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className={css.linkColumn}>
            <h4 className={css.columnTitle}>For owners</h4>
            <ul className={css.linkList}>
              {expertLinks.map(link => (
                <li key={link.label}>
                  <NamedLink name={link.name} params={link.params} className={css.link}>
                    {link.label}
                  </NamedLink>
                </li>
              ))}
            </ul>
          </div>

          <div className={css.linkColumn}>
            <h4 className={css.columnTitle}>Help</h4>
            <ul className={css.linkList}>
              <li>
                <NamedLink name="HowItWorksPage" className={css.link}>
                  How it works
                </NamedLink>
              </li>
              <li>
                <NamedLink name="FAQPage" className={css.link}>
                  Help center
                </NamedLink>
              </li>
              <li>
                <ExternalLink href="mailto:hello@donethat.com" className={css.link}>
                  Contact us
                </ExternalLink>
              </li>
            </ul>
          </div>

          <div className={css.linkColumn}>
            <h4 className={css.columnTitle}>Legal</h4>
            <ul className={css.linkList}>
              {LEGAL_LINKS.map(link => (
                <li key={link.name}>
                  <NamedLink name={link.name} className={css.link}>
                    {link.label}
                  </NamedLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* ===== Bottom: Meta ===== */}
        <div className={css.bottom}>
          <p className={css.copyright}>
            &copy; {year} {marketplaceName}.
          </p>
          <div className={css.social}>
            {facebookUrl && (
              <ExternalLink href={facebookUrl} className={css.socialLink} aria-label="Facebook">
                <IconSocialMediaFacebook />
              </ExternalLink>
            )}
            {instagramUrl && (
              <ExternalLink href={instagramUrl} className={css.socialLink} aria-label="Instagram">
                <IconSocialMediaInstagram />
              </ExternalLink>
            )}
            {tiktokUrl && (
              <ExternalLink href={tiktokUrl} className={css.socialLink} aria-label="TikTok">
                <IconSocialMediaTiktok />
              </ExternalLink>
            )}
            {twitterUrl && (
              <ExternalLink href={twitterUrl} className={css.socialLink} aria-label="Twitter">
                <IconSocialMediaTwitter />
              </ExternalLink>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
